import axios, { AxiosError, AxiosHeaderValue, AxiosRequestConfig } from "axios";
import { extractErrorMessage } from "./errors";
import { ApiError } from "./types";

const parseRetryAfter = (value: AxiosHeaderValue | undefined) => {
  if (typeof value !== "string" || !/^\d+$/.test(value.trim()))
    return undefined;
  return Number(value);
};

const createApiError = (error: unknown) => {
  const { message, status, details } = extractErrorMessage(error);
  const response = error instanceof AxiosError ? error.response : undefined;

  return new ApiError(message, status, {
    details,
    data: response?.data,
    retryAfterSeconds: parseRetryAfter(response?.headers?.["retry-after"]),
  });
};

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const getBearerToken = (config: AxiosRequestConfig) => {
  const authorization = config.headers?.Authorization;
  if (typeof authorization !== "string") return null;

  return authorization.startsWith("Bearer ")
    ? authorization.slice("Bearer ".length)
    : null;
};

/**
 * Creates an Axios instance with standard configuration and optional auth handling
 * @param apiUrl The base URL for the API
 * @param options Optional callbacks for token refresh
 * @returns Configured Axios instance
 */
export const createApiInstance = (
  apiUrl: string,
  options?: {
    attachAuthToken?: boolean;
    onRefreshToken?: () => Promise<string | null>;
  },
) => {
  const attachAuthToken = options?.attachAuthToken ?? true;
  let refreshPromise: Promise<string | null> | undefined;
  const instance = axios.create({
    baseURL: apiUrl,
    withCredentials: true,
  });

  const refreshToken = () => {
    if (!options?.onRefreshToken) return Promise.resolve(null);

    refreshPromise ??= options.onRefreshToken().finally(() => {
      refreshPromise = undefined;
    });

    return refreshPromise;
  };

  // Request interceptor
  instance.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem("token"); // Get the most current token
    if (attachAuthToken && currentToken && config.headers) {
      config.headers.Authorization = `Bearer ${currentToken}`;

      // Leave FormData bodies alone: forcing JSON would make axios serialize
      // the form to JSON, turning File entries into empty objects. Without a
      // Content-Type, axios sets the multipart/form-data boundary itself.
      if (!(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json";
      }
    }
    return config;
  });

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("token_refresh") &&
        !originalRequest.url?.includes("logout") &&
        options?.onRefreshToken
      ) {
        originalRequest._retry = true;

        const failedRequestToken = getBearerToken(originalRequest);
        const currentToken = localStorage.getItem("token");

        // Another request may have refreshed the token before this older 401
        // reached the interceptor. Replaying with the current token avoids a
        // second refresh rotating that token out from under in-flight replays.
        const replayToken =
          failedRequestToken &&
          currentToken &&
          failedRequestToken !== currentToken
            ? currentToken
            : await refreshToken();

        if (replayToken) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${replayToken}`,
          };
          return instance(originalRequest);
        }

        return Promise.reject(error);
      }

      // Transform any API error into our standard ApiError. The response body
      // is taken straight off the Axios error rather than through
      // extractErrorMessage, whose job is to reduce a body to one message:
      // some endpoints answer with structured data the caller has to act on (a
      // 409 upload conflict, say), and only the raw body carries it.
      throw createApiError(error);
    },
  );

  return instance;
};
